import { useCurrentUser } from "@qualio/ui-components";
import { useQuery } from "react-query";

import { QriDetails, qriApiClient } from "../api/qri";

type UseResolveQriDetails = {
  /** Resolved name and identifier code per QRI. Unresolvable QRIs are absent. */
  details: Record<string, QriDetails>;
  isLoading: boolean;
};

/**
 * Resolves a set of QRIs to their resource names and identifier codes via the
 * QRI service.
 *
 * The query key is derived from the QRIs themselves rather than the array
 * identity, so callers do not have to memoise what they pass in. Passing an
 * empty array disables the request.
 */
export const useResolveQriDetails = (
  qris: readonly string[],
): UseResolveQriDetails => {
  const { companyId } = useCurrentUser();
  const uniqueQris = Array.from(new Set(qris)).sort();

  const { data, isLoading } = useQuery(
    ["qri-details", companyId, uniqueQris.join(",")],
    () => qriApiClient.resolveQriDetails(uniqueQris),
    {
      refetchInterval: (data) =>
        data && Object.keys(data).length === uniqueQris.length ? false : 3000,
    },
  );

  return { details: data ?? {}, isLoading };
};
